// src/services/bookingService.js
import { 
  collection, 
  addDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  orderBy,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";

const bookingsCollectionRef = collection(db, "bookings");

// Add a new booking
export const addBooking = async (bookingData) => {
  try {
    // Ensure status is capitalized
    const bookingWithStatus = {
      ...bookingData,
      status: bookingData.status.charAt(0).toUpperCase() + bookingData.status.slice(1).toLowerCase(),
      createdAt: serverTimestamp()
    };

    const bookingRef = await addDoc(collection(db, 'bookings'), bookingWithStatus);
    return { success: true, id: bookingRef.id };
  } catch (error) {
    console.error("Error creating booking:", error);
    return { success: false, error: error.message };
  }
};

// Get all bookings (staff only)
export const getAllBookings = async () => {
  try {
    const bookingsRef = collection(db, 'bookings');
    const snapshot = await getDocs(bookingsRef);
    
    const bookings = [];
    snapshot.forEach((doc) => {
      bookings.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, bookings };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get bookings for a specific student
export const getStudentBookings = async (studentId) => {
  try {
    const bookingsRef = collection(db, 'bookings');
    const q = query(bookingsRef, where('studentId', '==', studentId));
    const snapshot = await getDocs(q);
    
    const bookings = [];
    snapshot.forEach((doc) => {
      bookings.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, bookings };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update a booking (status, checkout items, etc.)
export const updateBooking = async (bookingId, updates) => {
  try {
    const bookingRef = doc(db, 'bookings', bookingId);
    await updateDoc(bookingRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Delete a booking (for cancellations)
export const deleteBooking = async (bookingId) => {
  try {
    await deleteDoc(doc(db, 'bookings', bookingId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update checkout/checkin status
export const updateBookingChecks = async (bookingId, checkType, status) => {
  try {
    const bookingRef = doc(db, 'bookings', bookingId);
    await updateDoc(bookingRef, {
      [`checks.${checkType}`]: status,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update equipment on a booking
export const updateBookingEquipment = async (bookingId, equipmentId, checkType) => {
  try {
    const bookingRef = doc(db, 'bookings', bookingId);
    const updates = {
      updatedAt: serverTimestamp()
    };

    // Update the appropriate check array
    if (checkType === 'checkout') {
      updates[`checkedOutItems.${equipmentId}`] = true;
    } else if (checkType === 'checkin') {
      updates[`checkedInItems.${equipmentId}`] = true;
    }

    await updateDoc(bookingRef, updates);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update notes on a booking and set notesUpdatedByStaff
export const updateBookingNotes = async (bookingId, notes, isStaff = false) => {
  try {
    const bookingRef = doc(db, 'bookings', bookingId);
    await updateDoc(bookingRef, {
      notes,
      notesUpdatedByStaff: isStaff,
      notesLastUpdated: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};
